import {Locale, getLanguage, useConfig} from '@aboutyou/config';
import {useRequest} from '@aboutyou/data-fetcher';
import {useTrackFeature} from '@aboutyou/tracking';
import {useCallback} from 'react';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/AuthenticationProvider';

const TRACKER_NAME = 'customer_service_chat_bot';

export const useHelpCenterChatbot = () => {
  const [isOpen, setIsOpen] = useBooleanState('help_center_chatbot_is_open');
  const [isLoaded, setIsLoaded] = useBooleanState(
    'help_center_chatbot_is_loaded',
  );
  const [isShown, setIsShown] = useBooleanState('help_center_chatbot_is_shown');
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const trackFeature = useTrackFeature();
  const {chatbotConversationId} = useConfig();

  const open = useCallback(
    ({
      onCancel,
      onError,
    }: {onCancel?: VoidFunction; onError?: VoidFunction} = {}) => {
      // as the JWTs contain customer data we need to ensure that the current CO session is valid
      showUserAuthenticationPrompt({
        includeCheckoutSession: true,
        allowGuestLogin: false,
        onSuccess: () => {
          setIsOpen(true);
          setIsShown(true);
        },
        onCancel,
        onError,
      });
    },
    [showUserAuthenticationPrompt],
  );

  const openWidget = useCallback(() => {
    window.zE?.('messenger', 'open');

    if (!isLoaded) {
      // tracks the first time the chatbot is opened
      // since the open listener is not yet registered at this point
      trackOpen();
    }
  }, [isLoaded]);

  const close = useCallback(() => {
    window.zE?.('messenger', 'close');
    setIsOpen(false);
  }, []);

  const show = useCallback(() => {
    window.zE?.('messenger', 'show');
    setIsShown(true);
  }, []);

  const hide = useCallback(() => {
    window.zE?.('messenger', 'close');
    setIsOpen(false);
    setIsShown(false);
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen]);

  const logIn = useCallback((chatbotJwt: string) => {
    window.zE?.('messenger', 'loginUser', (callback: (jwt: string) => void) => {
      callback(chatbotJwt);
    });
  }, []);

  const logOut = useCallback(() => {
    window.zE?.('messenger', 'logoutUser');
    hide();
  }, []);

  const setLocale = useCallback((locale: Locale) => {
    window.zE?.('messenger:set', 'locale', getLanguage(locale));
  }, []);

  const setAybaJwt = useCallback((aybaJwt: string) => {
    window.zE?.('messenger:set', 'conversationFields', [
      {
        id: chatbotConversationId,
        value: aybaJwt,
      },
    ]);
  }, []);

  const setLoaded = useCallback(() => {
    if (!isLoaded) {
      setIsLoaded(true);
    }
  }, []);

  const registerOpenListener = useCallback((callback: VoidFunction) => {
    window.zE?.('messenger:on', 'open', () => {
      callback();
    });
  }, []);

  const registerCloseListener = useCallback((callback: VoidFunction) => {
    window.zE?.('messenger:on', 'close', () => {
      callback();
    });
  }, []);

  const registerUnreadMessagesListener = useCallback(
    (callback: (count: number) => void) => {
      window.zE?.('messenger:on', 'unreadMessages', (count: number) => {
        callback(count);
      });
    },
    [],
  );

  const trackOpen = useCallback(() => {
    trackFeature({
      eventType: 'open',
      label: TRACKER_NAME,
      name: TRACKER_NAME,
    });
  }, []);

  const trackClose = useCallback(() => {
    trackFeature({
      eventType: 'close',
      label: TRACKER_NAME,
      name: TRACKER_NAME,
    });
  }, []);

  return {
    isOpen,
    isLoaded,
    isShown,
    open,
    openWidget,
    close,
    show,
    hide,
    toggle,
    logIn,
    logOut,
    setLocale,
    setAybaJwt,
    setLoaded,
    registerOpenListener,
    registerCloseListener,
    registerUnreadMessagesListener,
    trackOpen,
    trackClose,
  };
};

const useBooleanState = (key: string): [boolean, (isOpen: boolean) => void] => {
  const {data, updateCache} = useRequest<{
    value: boolean;
  }>(
    key,
    () => ({
      value: false,
    }),
    {
      keepPreviousData: false,
    },
  );

  const setValue = useCallback((value: boolean) => {
    updateCache({value});
  }, []);

  return [data?.value ?? false, setValue];
};
